import { isValidIIN } from '@aviata/iin';
import { isObject, isString } from '@aviata/type-guards';
import { dateUtils } from 'shared';
import type { Accounts } from '@aviata/platform-types';

import { documentTypes, MAPPED_RAILWAYS_COUNTRIES } from './config';
import {
  Gender,
  FareType,
  type DocumentType,
  type PassengerData,
} from './types';

type ParsedIin = {
  dateOfBirth?: string;
  gender?: Gender;
};

export const parseIin = (value: string) => {
  const MALE_IDS = [1, 3, 5];
  const FEMALE_IDS = [2, 4, 6];

  const parsedIin: ParsedIin = {};
  if (!value || value.length !== 12 || !isValidIIN(value)) {
    return parsedIin;
  }

  const genderId = parseInt(value[6]);
  if (!MALE_IDS.includes(genderId) && !FEMALE_IDS.includes(genderId)) {
    return parsedIin;
  }

  const century = Math.floor((genderId - 1) / 2 + 18);
  const year = century * 100 + parseInt(value.substring(0, 2));

  parsedIin.dateOfBirth = `${value.substring(4, 6)}.${value.substring(
    2,
    4,
  )}.${year}`;
  if (MALE_IDS.includes(genderId)) {
    parsedIin.gender = Gender.M;
  } else {
    parsedIin.gender = Gender.F;
  }

  return parsedIin;
};

export const isIinRequired = (documentType: DocumentType) => {
  const { ID, PASSPORT_KZ } = documentTypes;
  // Array.includes is too narrow and does not allow super types to be passed in includes. https://github.com/microsoft/TypeScript/issues/26255
  return ([ID, PASSPORT_KZ] as DocumentType[]).includes(documentType);
};

export const isPassport = (documentType: DocumentType) => {
  return documentType === documentTypes.PASSPORT_KZ;
};

export const isPassportUzbekistan = (documentType: DocumentType) => {
  return documentType === documentTypes.PASSPORT_UZ;
};

export const isChild = (fareType: FareType) => {
  return fareType === FareType.CHILD;
};

export const isPassengerData = (data: unknown): data is PassengerData => {
  return (
    isObject(data) &&
    isString(data.firstName) &&
    isString(data.lastName) &&
    isString(data.fareType) &&
    isString(data.documentType) &&
    isString(data.citizenship) &&
    isString(data.gender) &&
    isString(data.dateOfBirth) &&
    isObject(data.iin) &&
    isObject(data.patronymic)
  );
};

export const mapCitizenship = (countryCode: string) =>
  MAPPED_RAILWAYS_COUNTRIES[countryCode];

const mapGender = (gender: Accounts.Passenger['gender']) => Gender[gender];
const mapFareType = (fareType?: string) => {
  switch (fareType) {
    case 'CHD':
      return FareType.CHILD;
    default:
      return FareType.ADULT;
  }
};

export function mapAccountsPassenger(
  passenger: Accounts.Passenger,
): PassengerData {
  const parsedIin = parseIin(passenger.national_id);
  const parsedGender = parsedIin.gender ?? '';
  const parsedDateOfBirth = parsedIin.dateOfBirth ?? '';
  const documentType = passenger.document_type as DocumentType;
  let documentNumber = passenger.document_number;
  if (
    isString(documentNumber) &&
    documentType === documentTypes.PASSPORT_KZ &&
    !documentNumber.toUpperCase().startsWith('N')
  ) {
    documentNumber = `N${documentNumber}`;
  }

  return {
    id: passenger.id,
    account: passenger.account,
    direction: passenger.direction,
    fareType: mapFareType(passenger.type),
    firstName: passenger.first_name.toUpperCase(),
    lastName: passenger.last_name.toUpperCase(),
    gender: mapGender(passenger.gender) ?? parsedGender,
    dateOfBirth: passenger.dob
      ? dateUtils.dateFromAccountsFormat(passenger.dob)
      : parsedDateOfBirth,
    documentNumber,
    documentType,
    citizenship: passenger.citizenship,
    patronymic: {
      value: passenger.patronymic.toUpperCase(),
      hasPatronymic: Boolean(passenger.patronymic),
    },
    iin: {
      value: passenger.national_id,
      hasIin: Boolean(passenger.national_id),
    },
  };
}

export function mapPassengerToAccountsPayload(
  passenger: PassengerData,
  options?: { withExtraDataRequired?: boolean; isFemaleCar?: boolean },
): Partial<Accounts.Passenger> {
  const type: Accounts.Passenger['type'] = isChild(passenger.fareType)
    ? 'CHD'
    : 'ADT';
  const gender: Accounts.Passenger['gender'] =
    passenger.gender === Gender.M ? 'M' : 'F';

  const withPatronymic =
    passenger.patronymic.hasPatronymic && passenger.patronymic.value;
  const withBirthDate =
    passenger.dateOfBirth &&
    (isChild(passenger.fareType) || options?.withExtraDataRequired);
  const withIin = passenger.iin.hasIin && passenger.iin.value;
  const withGender =
    (options?.withExtraDataRequired || options?.isFemaleCar) &&
    passenger.gender;
  const withCitizenship =
    (options?.withExtraDataRequired || options?.isFemaleCar) &&
    passenger.citizenship;

  return {
    id: passenger.id ? passenger.id : undefined,
    account: passenger.account ? passenger.account : undefined,
    first_name: passenger.firstName,
    last_name: passenger.lastName,
    patronymic: withPatronymic ? passenger.patronymic.value : '',
    dob: withBirthDate
      ? dateUtils.dateToAccountsFormat(passenger.dateOfBirth)
      : undefined,
    type,
    document_number: passenger.documentNumber,
    document_type: passenger.documentType,
    national_id: withIin ? passenger.iin.value : '',
    // rw serializer doesn't accept values for this field
    document_expires_at: null,
    // TODO: add blank option to Accounts.Passenger
    // @ts-expect-error gender field is required by accounts, but blank field is an acceptable option
    gender: withGender ? gender : '',
    citizenship: withCitizenship ? passenger.citizenship : undefined,
    direction: passenger.direction ?? 'rwflow',
  };
}
